import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { format, addDays, subDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from 'date-fns';

interface DailySliderProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  dailyTotals: Array<{
    date: string;
    class_count: number;
  }>;
}

export const DailySlider = ({ 
  selectedDate, 
  onDateSelect, 
  dailyTotals 
}: DailySliderProps) => {
  const { t } = useI18n();
  const [visibleDays, setVisibleDays] = useState<Date[]>([]);

  const dayNames = [
    t('calendar.days.sun'),
    t('calendar.days.mon'),
    t('calendar.days.tue'),
    t('calendar.days.wed'),
    t('calendar.days.thu'),
    t('calendar.days.fri'),
    t('calendar.days.sat')
  ];

  useEffect(() => {
    // Generate visible days (current week and adjacent weeks)
    const days = [];
    const currentDate = new Date();
    
    // Add previous week
    const prevWeekStart = startOfWeek(subDays(currentDate, 7));
    const prevWeekEnd = endOfWeek(subDays(currentDate, 7));
    const prevWeekDays = eachDayOfInterval({ start: prevWeekStart, end: prevWeekEnd });
    days.push(...prevWeekDays);
    
    // Add current week
    const currentWeekStart = startOfWeek(currentDate);
    const currentWeekEnd = endOfWeek(currentDate);
    const currentWeekDays = eachDayOfInterval({ start: currentWeekStart, end: currentWeekEnd });
    days.push(...currentWeekDays);
    
    // Add next week
    const nextWeekStart = startOfWeek(addDays(currentDate, 7));
    const nextWeekEnd = endOfWeek(addDays(currentDate, 7));
    const nextWeekDays = eachDayOfInterval({ start: nextWeekStart, end: nextWeekEnd });
    days.push(...nextWeekDays);
    
    setVisibleDays(days);
    
    // Auto-scroll to selected date after a short delay
    setTimeout(() => {
      scrollToSelectedDate();
    }, 100);
  }, []);

  const getClassCount = (date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    const dayData = dailyTotals.find(d => d.date === dateString);
    
    return dayData ? dayData.class_count : 0;
  };

  const scrollToSelectedDate = () => {
    const selectedIndex = visibleDays.findIndex(date => isSameDay(date, selectedDate));
    
    if (selectedIndex !== -1) {
      const container = document.getElementById('daily-slider-container');
      const item = document.getElementById(`day-item-${selectedIndex}`);
      
      if (container && item) {
        const containerWidth = container.offsetWidth;
        const itemWidth = item.offsetWidth;
        const scrollLeft = item.offsetLeft - (containerWidth / 2) + (itemWidth / 2);
        
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    scrollToSelectedDate();
  }, [selectedDate, visibleDays.length]);

  const handlePreviousWeek = () => {
    const newDate = subDays(selectedDate, 7);
    onDateSelect(newDate);
  };

  const handleNextWeek = () => {
    const newDate = addDays(selectedDate, 7);
    onDateSelect(newDate);
  };

  return (
    <div className="w-full mb-4">

      <div 
        id="daily-slider-container"
        className="flex gap-6 overflow-x-auto scrollbar-hide px-2 py-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {visibleDays.map((date, index) => {
          const isSelected = isSameDay(date, selectedDate);
          const classCount = getClassCount(date);
          const dayName = dayNames[date.getDay()];
          const dayNumber = date.getDate();

          return (
            <div
              key={index}
              id={`day-item-${index}`}
              className={`flex flex-col items-center min-w-[50px] cursor-pointer transition-all duration-200 ${
                isSelected ? 'scale-105' : 'hover:scale-102'
              }`}
              onClick={() => onDateSelect(date)}
            >
              {/* Day name */}
              <span className={`text-xs font-bold mb-1 ${
                isSelected ? 'text-white' : 'text-white/60'
              }`}>
                {dayName}
              </span>
              
              {/* Day number */}
              <span className={`text-lg font-bold mb-1 ${
                isSelected ? 'text-white' : 'text-white'
              }`}>
                {dayNumber.toString().padStart(2, '0')}
              </span>
              
              {/* Class count */}
              <div className={`flex items-center gap-1 ${
                isSelected ? 'text-white' : 'text-white/40'
              }`}>
                <span className="text-[10px] font-medium">
                  {classCount.toString().padStart(2, '0')}
                </span>
                <span className="text-[10px]">
                  {classCount === 1 ? t('calendar.class') : t('calendar.classes')}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
